import { useSyncExternalStore } from 'react';

/**
 * How this browser can install the app (#303, part 2), as one value the account menu reads.
 *
 * - `prompt`: Chrome, Edge and Android have offered to install, and kept the offer for us
 *   (`beforeinstallprompt`). Our button asks the browser to show its own dialog.
 * - `ios-guide`: iPhone and iPad offer no event. Every browser there installs from the Share menu,
 *   so the button opens a short guide instead.
 * - `mac-guide`: Safari on macOS (Sonoma and later) installs from File → Add to Dock, also with
 *   no event.
 * - `installed`: we are already running as the installed app. Nothing to offer.
 * - `none`: the browser cannot install, or has not offered yet. Nothing to offer either.
 */
export type InstallRoute = 'prompt' | 'ios-guide' | 'mac-guide' | 'installed' | 'none';

/** The non-standard event Chromium fires when the page is installable. */
export type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

export type InstallEnvironment = {
  userAgent: string;
  maxTouchPoints: number;
  standalone: boolean;
};

let deferredPrompt: BeforeInstallPromptEvent | null = null;
let installedNow = false;
const listeners = new Set<() => void>();

function notify(): void {
  listeners.forEach((listener) => listener());
}

/**
 * Keeps the browser's install offer for later, from the very first script run (`main.tsx`): the
 * event can fire before React mounts, and a listener added by a component would miss it.
 * Without `preventDefault` Chrome shows its own mini-infobar on Android, which we replace with
 * the account menu's entry.
 */
export function captureInstallPrompt(target: Window = window): void {
  target.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    deferredPrompt = event as BeforeInstallPromptEvent;
    notify();
  });
  target.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    installedNow = true;
    notify();
  });
}

export function currentInstallEnvironment(): InstallEnvironment {
  const standalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return {
    userAgent: navigator.userAgent,
    maxTouchPoints: navigator.maxTouchPoints ?? 0,
    standalone,
  };
}

/** Pure, so the tests can walk every platform without a browser. */
export function installRouteFor(
  environment: InstallEnvironment,
  hasPrompt: boolean,
  installed = false,
): InstallRoute {
  if (environment.standalone || installed) {
    return 'installed';
  }
  if (hasPrompt) {
    return 'prompt';
  }
  const ua = environment.userAgent;
  // iPadOS asks for the desktop site, so an iPad says «Macintosh»: the touch screen gives it away.
  const iPad = /Macintosh/.test(ua) && environment.maxTouchPoints > 1;
  if (/iPhone|iPad|iPod/.test(ua) || iPad) {
    return 'ios-guide';
  }
  const safari = /Safari\//.test(ua) && !/Chrome|Chromium|CriOS|Edg|Firefox|FxiOS|OPR/.test(ua);
  if (/Macintosh/.test(ua) && safari) {
    return 'mac-guide';
  }
  return 'none';
}

/**
 * Shows the browser's own install dialog. The offer can be used once only, so it is dropped
 * whatever the person answers; Chromium fires a new one later if they declined.
 */
export async function promptInstall(): Promise<void> {
  const event = deferredPrompt;
  if (event === null) {
    return;
  }
  deferredPrompt = null;
  notify();
  await event.prompt();
  await event.userChoice.catch(() => undefined);
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function snapshot(): InstallRoute {
  return installRouteFor(currentInstallEnvironment(), deferredPrompt !== null, installedNow);
}

export function useInstallRoute(): InstallRoute {
  return useSyncExternalStore(subscribe, snapshot, () => 'none');
}

/** Tests only: forget a captured offer between cases. */
export function resetInstallStateForTests(): void {
  deferredPrompt = null;
  installedNow = false;
  notify();
}
